import React from "react";
import { cn } from "../../lib/utils";

/**
 * A two-pane content card component.
 * Left: image/icon placeholder block
 * Right: text content area with title, subtitle, and description placeholders
 */
const DARK_COLOR = "#1E1E1EFF";

const ContentCard = ({
  className,
  imageSrc,
  imageAlt,
  title,
  subtitle,
  description,
  children,
  style,
  variant = "light", // "light" | "dark" - dark uses #1E1E1EFF instead of gray
  ...props
}) => {
  const isDark = variant === "dark";
  const placeholderColor = isDark ? DARK_COLOR : undefined;

  return (
    <div
      className={cn(
        "flex overflow-hidden rounded shadow-sm bg-white border border-gray-200",
        className,
      )}
      style={{ width: 79, height: 40, ...style }}
      {...props}
    >
      {/* Left section - image/icon placeholder */}
      <div
        className={cn(
          "flex-shrink-0 w-1/3 min-w-0 rounded-l",
          !placeholderColor && "bg-gray-200",
        )}
        style={
          placeholderColor ? { backgroundColor: placeholderColor } : undefined
        }
      >
        {imageSrc ? (
          <img
            src={imageSrc}
            alt={imageAlt || ""}
            className="w-full h-full object-cover rounded-l"
          />
        ) : (
          <div className="w-full h-full rounded-l" />
        )}
      </div>

      {/* Right section - content area */}
      <div className="flex-1 p-1 flex flex-col justify-center gap-0.5 min-w-0 overflow-hidden">
        {children ? (
          children
        ) : (
          <>
            {/* Title - longest line or placeholder */}
            {title ? (
              <p className="text-gray-800 font-medium text-[8px] leading-tight line-clamp-1 truncate">
                {title}
              </p>
            ) : (
              <div
                className={cn(
                  "h-1 rounded-sm w-4/5",
                  !placeholderColor && "bg-gray-300",
                )}
                style={
                  placeholderColor
                    ? { backgroundColor: placeholderColor }
                    : undefined
                }
              />
            )}
            {/* Subtitle - medium line or placeholder */}
            {subtitle ? (
              <p className="text-gray-600 text-[8px] leading-tight line-clamp-1 truncate">
                {subtitle}
              </p>
            ) : (
              <div
                className={cn(
                  "h-0.5 rounded-sm w-2/3",
                  !placeholderColor && "bg-gray-200",
                )}
                style={
                  placeholderColor
                    ? { backgroundColor: placeholderColor }
                    : undefined
                }
              />
            )}
            {/* Description - shortest line or placeholder */}
            {description ? (
              <p className="text-gray-500 text-[6px] leading-tight line-clamp-1 truncate">
                {description}
              </p>
            ) : (
              <div
                className={cn(
                  "h-0.5 rounded-sm w-1/2",
                  !placeholderColor && "bg-gray-200",
                )}
                style={
                  placeholderColor
                    ? { backgroundColor: placeholderColor }
                    : undefined
                }
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ContentCard;
